/**
 * AGREEMENTS, under Everything else > Admin.
 *
 * Anthony, 2026-10-16: "take the ... quote and docusign off every
 * supercanic shop desk lead ... it was only supposed to be on his ... for
 * other docusign agreements just make it a button in the everything else
 * option under admin options."
 *
 * So outside a case file (NYIA, trade.ts caseFile) there is no agreement
 * card on a lead or a job. The agreement lives here: type a name and a
 * mobile, and the same card the call desk uses opens for that person
 * (esign-card.ts, mountEsignForCaller). Below it, every agreement the shop
 * has sent, and a tap on one opens its card to countersign or withdraw.
 */

import { esc, mount, since } from "../ui/dom.ts";
import { authed, mountEsignForCaller } from "./esign-card.ts";

type Row = { id: string; title: string; status: string; status_words: string; signer_name: string;
  signer_phone10: string | null; sent_at: string; sent_by_name: string | null };

const ten = (s: string) => { const d = s.replace(/\D/g, ""); return d.length === 11 && d.startsWith("1") ? d.slice(1) : d; };
const phone = (p: string | null) => p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : "";
const PILL: Record<string, string> = { completed: "s-paid", client_signed: "s-paid", declined: "s-cancelled",
  voided: "s-cancelled", expired: "s-cancelled" };

export async function agreementsPage(): Promise<void> {
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>Agreements</h1>
        <p>Send an agreement to sign, and see every one that has gone out.</p></header>
      <div class="card">
        <div class="card-head">Send one</div>
        <div class="pad esign-form">
          <label class="fine">Their name <input type="text" id="ag-name" autocomplete="off"></label>
          <label class="fine">Their mobile <input type="tel" id="ag-phone" inputmode="tel" autocomplete="off"></label>
          <div class="row"><button class="btn primary" id="ag-open">Open the agreement</button></div>
          <div class="fine" id="ag-msg"></div>
        </div>
      </div>
      <div class="card" id="esign-card" hidden></div>
      <div class="card" id="ag-list"><div class="loading">Reading&hellip;</div></div>
    </div>`);

  const card = document.getElementById("esign-card") as HTMLElement;
  const open = (p10: string, name: string) => {
    void mountEsignForCaller(card, p10, name);
    card.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  document.getElementById("ag-open")?.addEventListener("click", () => {
    const name = (document.getElementById("ag-name") as HTMLInputElement).value.trim();
    const p10 = ten((document.getElementById("ag-phone") as HTMLInputElement).value);
    const msg = document.getElementById("ag-msg")!;
    if (!name) { msg.textContent = "Type their name first. It goes on the agreement."; return; }
    if (p10.length !== 10) { msg.textContent = "They need a ten digit mobile number."; return; }
    msg.textContent = "";
    open(p10, name);
  });

  const host = document.getElementById("ag-list")!;
  let rows: Row[] = [];
  let on = false;
  try {
    const r = await authed("/api/esign/staff/list");
    if (r.status === 403) { host.innerHTML = `<div class="pad fine">Only the owner, a supervisor or an advisor can send agreements.</div>`; return; }
    const j = await r.json() as { on: boolean; envelopes: Row[] };
    on = j.on; rows = j.envelopes ?? [];
  } catch { host.innerHTML = `<div class="pad fine">Could not read the agreements. Try again in a moment.</div>`; return; }
  if (!on) {
    host.innerHTML = `<div class="pad fine">Agreements are not switched on for this shop. Turn on "esign" in Automations, with an agreement on file.</div>`;
    return;
  }
  host.innerHTML = `<div class="card-head">${rows.length} sent</div>` + (rows.length ? rows.map((e) => `
    <button class="row ag-row" type="button" data-phone="${esc(e.signer_phone10 ?? "")}" data-name="${esc(e.signer_name)}">
      <div class="row-main">
        <div class="row-title"><span class="num">${esc(e.signer_name)}</span>
          <span class="pill ${PILL[e.status] ?? "s-scheduled"}">${esc(e.status_words)}</span></div>
        <div class="row-sub">${esc(e.title)}${e.signer_phone10 ? `<span class="dot">&middot;</span>${esc(phone(e.signer_phone10))}` : ""}${
          e.sent_by_name ? `<span class="dot">&middot;</span>sent by ${esc(e.sent_by_name)}` : ""}</div>
      </div>
      <div class="row-side"><div class="when">${esc(since(e.sent_at))}</div></div>
    </button>`).join("") : `<div class="pad fine">Nothing sent yet.</div>`);
  host.querySelectorAll<HTMLButtonElement>(".ag-row").forEach((b) => b.addEventListener("click", () => {
    if (b.dataset["phone"]) open(b.dataset["phone"], b.dataset["name"] ?? "");
  }));
}
